import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { CaseTypeValue, SortByValue, DateRangeValue } from '@shared/types/searchTypes';
import { CaseType, SortBy, DateRange } from '@shared/constants/searchConstants';

/**
 * Determines the localStorage key for the search store based on the current hostname.
 * This prevents cross-portal state pollution when running multiple portals on the same domain.
 *
 * Detection order:
 * 1. Check hostname for 'lexzone' -> 'lexzone-search'
 * 2. Default to 'lexlabour-search'
 */
const getStorageKey = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.includes('lexzone')) {
      return 'lexzone-search';
    }
  }
  return 'lexlabour-search';
};

export interface SearchFilters {
  query: string;
  courts: string[];
  domains: string[];
  themas: string[];
  wetsartikelen: string[];
  caseType: CaseTypeValue;
  dateRange: DateRangeValue;
  customDateFrom?: Date;
  customDateTo?: Date;
  sortBy: SortByValue;
}

interface FilterMetadata {
  courtNames: Record<string, string>;
  domainNames: Record<string, string>;
  themaNames: Record<string, string>;
  wetsartikelenNames: Record<string, string>;
  caseTypeNames: Record<string, string>;
  isLoadingMetadata: boolean;
  metadataError: string | null;
}

export interface SearchUIState {
  expandedSections: string[];
  expandedWetboeken: string[];
  expandedArtikelen: string[];
  expandedThemas: string[];
  expandedInstantieNiveaus: string[];
}

interface SearchStore {
  // Filter state
  filters: SearchFilters;
  metadata: FilterMetadata;

  // UI state
  uiState: SearchUIState;
  hasSearched: boolean;
  drawerOpen: boolean;

  // Pagination
  currentPage: number;
  itemsPerPage: number;

  // Actions
  setFilters: (filters: Partial<SearchFilters>) => void;
  setFiltersWithoutPageReset: (filters: Partial<SearchFilters>) => void;
  updateFilter: <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => void;
  clearFilters: () => void;
  resetFilters: () => void;

  // Metadata actions
  setCourtName: (id: string, name: string) => void;
  setDomainName: (id: string, name: string) => void;
  setThemaName: (id: string, name: string) => void;
  setWetsartikelName: (id: string, name: string) => void;
  setCaseTypeName: (id: string, name: string) => void;
  clearMetadata: () => void;
  setMetadataLoading: (loading: boolean) => void;
  setMetadataError: (error: string | null) => void;

  // Pagination actions
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (items: number) => void;

  // UI actions
  setHasSearched: (searched: boolean) => void;
  setDrawerOpen: (open: boolean) => void;
  toggleSection: (sectionKey: string) => void;
  toggleWetboek: (wetboekKey: string) => void;
  toggleArtikel: (artikelKey: string) => void;
  toggleThema: (themaKey: string) => void;
  toggleInstantieNiveau: (niveauKey: string) => void;
  resetUIState: () => void;

  // Computed values
  hasActiveFilters: () => boolean;
  getActiveFilterCount: () => number;
}

const defaultFilters: SearchFilters = {
  query: '',
  courts: [],
  domains: [],
  themas: [],
  wetsartikelen: [],
  caseType: CaseType.ALL,
  dateRange: DateRange.ALL,
  customDateFrom: undefined,
  customDateTo: undefined,
  sortBy: SortBy.DATE_DESC,
};

const defaultMetadata: FilterMetadata = {
  courtNames: {},
  domainNames: {},
  themaNames: {},
  wetsartikelenNames: {},
  caseTypeNames: {},
  isLoadingMetadata: false,
  metadataError: null,
};

const defaultUIState: SearchUIState = {
  expandedSections: ['periode', 'rechtsgebieden', 'instanties', 'type', 'themas', 'wetsartikelen'],
  expandedWetboeken: [],
  expandedArtikelen: [],
  expandedThemas: [],
  expandedInstantieNiveaus: [],
};

/**
 * Migrate persisted state for backward compatibility and Date rehydration.
 *
 * JSON.stringify converts Date objects to ISO strings. JSON.parse does NOT
 * convert them back, so customDateFrom/customDateTo become plain strings
 * after a page reload. This function restores them to proper Date objects.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function migratePersistedState(persistedState: any): any {
  if (!persistedState) return persistedState;

  // Ensure filters exist with defaults
  if (!persistedState.filters) {
    persistedState.filters = defaultFilters;
  }

  // Rehydrate Date objects from JSON strings.
  if (persistedState.filters.customDateFrom) {
    const parsed = new Date(persistedState.filters.customDateFrom);
    persistedState.filters.customDateFrom = isNaN(parsed.getTime()) ? undefined : parsed;
  }
  if (persistedState.filters.customDateTo) {
    const parsed = new Date(persistedState.filters.customDateTo);
    persistedState.filters.customDateTo = isNaN(parsed.getTime()) ? undefined : parsed;
  }

  // Ensure currentPage exists
  if (typeof persistedState.currentPage === 'undefined') {
    persistedState.currentPage = 1;
  }

  // Ensure itemsPerPage exists
  if (typeof persistedState.itemsPerPage === 'undefined') {
    persistedState.itemsPerPage = 10;
  }

  // Ensure hasSearched exists
  if (typeof persistedState.hasSearched === 'undefined') {
    persistedState.hasSearched = true;
  }

  // Ensure uiState exists with expandedInstantieNiveaus
  if (!persistedState.uiState) {
    persistedState.uiState = defaultUIState;
  } else if (!persistedState.uiState.expandedInstantieNiveaus) {
    persistedState.uiState.expandedInstantieNiveaus = [];
  }

  return persistedState;
}

export const useSearchStore = create<SearchStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        filters: defaultFilters,
        metadata: defaultMetadata,
        uiState: defaultUIState,
        currentPage: 1,
        itemsPerPage: 10,
        hasSearched: true, // Always true to show results
        drawerOpen: false,

      // Filter actions
      setFilters: (newFilters) =>
        set((state) => {
          const updatedFilters = { ...state.filters, ...newFilters };

          // Auto-switch to relevance sort when a search query is entered
          if ('query' in newFilters) {
            const hasQuery = newFilters.query && newFilters.query.trim() !== '';
            const hadQuery = state.filters.query && state.filters.query.trim() !== '';

            if (hasQuery && !hadQuery) {
              // Query was added: switch to relevance
              updatedFilters.sortBy = SortBy.RELEVANCE;
            } else if (!hasQuery && hadQuery) {
              // Query was cleared: switch back to date descending
              updatedFilters.sortBy = SortBy.DATE_DESC;
            }
          }

          return {
            filters: updatedFilters,
            currentPage: 1, // Reset to first page when filters change
          };
        }),

      setFiltersWithoutPageReset: (newFilters) =>
        set((state) => {
          const updatedFilters = { ...state.filters, ...newFilters };

          // Auto-switch to relevance sort when a search query is entered
          if ('query' in newFilters) {
            const hasQuery = newFilters.query && newFilters.query.trim() !== '';
            const hadQuery = state.filters.query && state.filters.query.trim() !== '';

            if (hasQuery && !hadQuery) {
              updatedFilters.sortBy = SortBy.RELEVANCE;
            } else if (!hasQuery && hadQuery) {
              updatedFilters.sortBy = SortBy.DATE_DESC;
            }
          }

          return {
            filters: updatedFilters,
            // Don't reset page - used for URL sync
          };
        }),

      updateFilter: (key, value) =>
        set((state) => {
          const updatedFilters = { ...state.filters, [key]: value };

          // Auto-switch sort when query changes
          if (key === 'query') {
            const hasQuery = typeof value === 'string' && value.trim() !== '';
            const hadQuery = state.filters.query && state.filters.query.trim() !== '';

            if (hasQuery && !hadQuery) {
              updatedFilters.sortBy = SortBy.RELEVANCE;
            } else if (!hasQuery && hadQuery) {
              updatedFilters.sortBy = SortBy.DATE_DESC;
            }
          }

          return {
            filters: updatedFilters,
            currentPage: 1, // Reset to first page when filter changes
          };
        }),

      clearFilters: () =>
        set({
          filters: defaultFilters,
          currentPage: 1,
        }),

      resetFilters: () =>
        set({
          filters: defaultFilters,
          metadata: defaultMetadata,
          currentPage: 1,
        }),

      // Metadata actions
      setCourtName: (id, name) =>
        set((state) => ({
          metadata: {
            ...state.metadata,
            courtNames: { ...state.metadata.courtNames, [id]: name },
          },
        })),

      setDomainName: (id, name) =>
        set((state) => ({
          metadata: {
            ...state.metadata,
            domainNames: { ...state.metadata.domainNames, [id]: name },
          },
        })),

      setThemaName: (id, name) =>
        set((state) => ({
          metadata: {
            ...state.metadata,
            themaNames: { ...state.metadata.themaNames, [id]: name },
          },
        })),

      setWetsartikelName: (id, name) =>
        set((state) => ({
          metadata: {
            ...state.metadata,
            wetsartikelenNames: { ...state.metadata.wetsartikelenNames, [id]: name },
          },
        })),

      setCaseTypeName: (id, name) =>
        set((state) => ({
          metadata: {
            ...state.metadata,
            caseTypeNames: { ...state.metadata.caseTypeNames, [id]: name },
          },
        })),

      clearMetadata: () =>
        set({ metadata: defaultMetadata }),

      setMetadataLoading: (loading) =>
        set((state) => ({
          metadata: { ...state.metadata, isLoadingMetadata: loading },
        })),

      setMetadataError: (error) =>
        set((state) => ({
          metadata: { ...state.metadata, metadataError: error },
        })),

      // Pagination actions
      setCurrentPage: (page) => set({ currentPage: page }),
      setItemsPerPage: (items) => set({ itemsPerPage: items, currentPage: 1 }),

      // UI actions
      setHasSearched: (searched) => set({ hasSearched: searched }),
      setDrawerOpen: (open: boolean) => set({ drawerOpen: open }),

      toggleSection: (sectionKey: string) =>
        set((state: SearchStore) => {
          const sections = state.uiState.expandedSections;
          const newSections = sections.includes(sectionKey)
            ? sections.filter((s: string) => s !== sectionKey)
            : [...sections, sectionKey];
          return {
            uiState: { ...state.uiState, expandedSections: newSections }
          };
        }),

      toggleWetboek: (wetboekKey: string) =>
        set((state: SearchStore) => {
          const wetboeken = state.uiState.expandedWetboeken;
          const newWetboeken = wetboeken.includes(wetboekKey)
            ? wetboeken.filter((w: string) => w !== wetboekKey)
            : [...wetboeken, wetboekKey];
          return {
            uiState: { ...state.uiState, expandedWetboeken: newWetboeken }
          };
        }),

      toggleArtikel: (artikelKey: string) =>
        set((state: SearchStore) => {
          const artikelen = state.uiState.expandedArtikelen;
          const newArtikelen = artikelen.includes(artikelKey)
            ? artikelen.filter((a: string) => a !== artikelKey)
            : [...artikelen, artikelKey];
          return {
            uiState: { ...state.uiState, expandedArtikelen: newArtikelen }
          };
        }),

      toggleThema: (themaKey: string) =>
        set((state: SearchStore) => {
          const themas = state.uiState.expandedThemas;
          const newThemas = themas.includes(themaKey)
            ? themas.filter((t: string) => t !== themaKey)
            : [...themas, themaKey];
          return {
            uiState: { ...state.uiState, expandedThemas: newThemas }
          };
        }),

      toggleInstantieNiveau: (niveauKey: string) =>
        set((state: SearchStore) => {
          const niveaus = state.uiState.expandedInstantieNiveaus || [];
          const newNiveaus = niveaus.includes(niveauKey)
            ? niveaus.filter((n: string) => n !== niveauKey)
            : [...niveaus, niveauKey];
          return {
            uiState: { ...state.uiState, expandedInstantieNiveaus: newNiveaus }
          };
        }),

      resetUIState: () =>
        set({ uiState: defaultUIState }),

      // Computed values
      hasActiveFilters: () => {
        const { filters } = get();
        return (
          filters.query !== '' ||
          filters.courts.length > 0 ||
          filters.domains.length > 0 ||
          filters.themas.length > 0 ||
          filters.wetsartikelen.length > 0 ||
          filters.caseType !== CaseType.ALL ||
          filters.dateRange !== DateRange.ALL
        );
      },

      getActiveFilterCount: () => {
        const { filters } = get();
        let count = 0;

        if (filters.query) count++;
        if (filters.courts.length > 0) count += filters.courts.length;
        if (filters.domains.length > 0) count += filters.domains.length;
        if (filters.themas.length > 0) count += filters.themas.length;
        if (filters.wetsartikelen.length > 0) count += filters.wetsartikelen.length;
        if (filters.caseType !== CaseType.ALL) count++;
        if (filters.dateRange !== DateRange.ALL) count++;

        return count;
      },
      }),
      {
        name: getStorageKey(), // Dynamic localStorage key based on portal
        partialize: (state: SearchStore) => ({
          // Persist ALL search state - Zustand is the single source of truth
          filters: state.filters, // All filter state (query, courts, domains, themas, wetsartikelen, dateRange, etc.)
          currentPage: state.currentPage, // Pagination state
          itemsPerPage: state.itemsPerPage, // Items per page preference
          hasSearched: state.hasSearched, // Search initiation state
          uiState: state.uiState, // UI state (expanded sections, etc.)
          // NOTE: metadata is NOT persisted (courtNames, domainNames, etc.) - fetched fresh on each load
        }),
        migrate: (persistedState: any, _version: number) => migratePersistedState(persistedState),
      }
    ),
    {
      name: getStorageKey(), // Redux DevTools name (matches localStorage key)
    }
  )
);

// Selector hooks for common use cases
export const useSearchFilters = () => useSearchStore((state: SearchStore) => state.filters);
export const useSearchMetadata = () => useSearchStore((state: SearchStore) => state.metadata);
export const useSearchPagination = () => useSearchStore((state: SearchStore) => ({
  currentPage: state.currentPage,
  itemsPerPage: state.itemsPerPage,
  setCurrentPage: state.setCurrentPage,
  setItemsPerPage: state.setItemsPerPage,
}));
export const useSearchUI = () => useSearchStore((state: SearchStore) => ({
  hasSearched: state.hasSearched,
  drawerOpen: state.drawerOpen,
  setHasSearched: state.setHasSearched,
  setDrawerOpen: state.setDrawerOpen,
}));